import jsQR from 'jsqr';
import { useEffect, useRef, useState } from 'react';

interface Props {
  /** Called with the decoded QR string. The parent decides what to do with it. */
  onDetected: (value: string) => void;
  /** Debounce window so one physical scan doesn't fire repeatedly (ms). */
  cooldownMs?: number;
}

/**
 * Live camera QR scanner. Uses getUserMedia (rear camera when available) and
 * decodes frames with jsQR on a hidden canvas. Cleans up the stream on unmount.
 */
export default function QrScanner({ onDetected, cooldownMs = 2500 }: Props) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [error, setError] = useState('');
  const [ready, setReady] = useState(false);
  const lastHit = useRef<{ value: string; at: number }>({ value: '', at: 0 });

  useEffect(() => {
    let stream: MediaStream | null = null;
    let raf = 0;
    let stopped = false;

    const tick = () => {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      if (!stopped && video && canvas && video.readyState === video.HAVE_ENOUGH_DATA) {
        const w = video.videoWidth;
        const h = video.videoHeight;
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        if (ctx && w && h) {
          ctx.drawImage(video, 0, 0, w, h);
          const img = ctx.getImageData(0, 0, w, h);
          const found = jsQR(img.data, w, h, { inversionAttempts: 'dontInvert' });
          if (found?.data) {
            const now = Date.now();
            const isRepeat = found.data === lastHit.current.value && now - lastHit.current.at < cooldownMs;
            if (!isRepeat) {
              lastHit.current = { value: found.data, at: now };
              onDetected(found.data.trim());
            }
          }
        }
      }
      if (!stopped) raf = requestAnimationFrame(tick);
    };

    (async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: 'environment' } },
          audio: false,
        });
        if (stopped) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        const video = videoRef.current!;
        video.srcObject = stream;
        await video.play();
        setReady(true);
        raf = requestAnimationFrame(tick);
      } catch (err) {
        setError(
          err instanceof DOMException && err.name === 'NotAllowedError'
            ? 'ไม่ได้รับอนุญาตให้ใช้กล้อง — อนุญาตในเบราว์เซอร์แล้วลองใหม่'
            : 'เปิดกล้องไม่สำเร็จ — ใช้การพิมพ์/วางรหัสแทนได้',
        );
      }
    })();

    return () => {
      stopped = true;
      cancelAnimationFrame(raf);
      stream?.getTracks().forEach((t) => t.stop());
    };
  }, [onDetected, cooldownMs]);

  if (error) return <div className="form-error">{error}</div>;

  return (
    <div className="qr-scanner">
      <video ref={videoRef} muted playsInline />
      <div className="qr-scanner-frame" />
      {!ready && <div className="qr-scanner-hint">กำลังเปิดกล้อง…</div>}
      <canvas ref={canvasRef} style={{ display: 'none' }} />
    </div>
  );
}
